/**
 * Home → what is assigned to you (12 Oct 2026; restyled 14 Oct 2026 to the “Warm sand, final”
 * handoff, option 4a). Left, the Continue card — the course you most recently opened that is still
 * in progress (a quiz not passed counts) — the whole card opens it. Right, “Up next” (21 Oct 2026 —
 * was “This week”, which hid a course with no due date or one due later): the courses still to do,
 * overdue first, then soonest due, then those with no due date (newest first), up to three. Shown only to someone with courses assigned;
 * the full list is the Courses page, “For me”.
 *
 *   both          two columns, about 1.5 : 1, equal height
 *   no Up next    the Continue card, full width and shorter
 *   none going    Up next alone, full width
 *   < 1100px      stacked (side by side is too tight below that, beside the sidebar); on a phone the card's button drops under the text, full width
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, BookOpen } from 'lucide-react'
import { useOrgs } from '@/lib/useOrgs'
import { useMyLearning } from '@/lib/useMyLearning'
import { LEARNING_HOME } from '@/lib/learningHome'
import { spaLink } from '@/lib/spaLink'
import { learnState } from '@/lib/courseEntries'
import type { LearningItem } from '@/lib/orgApi'

const DAY = 86_400_000
const dayMonth = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
const due = (iso: string, now: number) => {
  const days = (Date.parse(iso) - now) / DAY
  return days >= 0 && days < 7 ? new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '') : dayMonth(iso)
}
// Reached the end with the quiz failed: not completed (D1, 13 Oct 2026) — the server says so.
const notPassed = (i: LearningItem) => !!i.quizNotPassed
const slides = (i: LearningItem) => i.versions.find(v => v.courseId === i.progress?.versionCourseId)?.slideCount ?? i.versions[0]?.slideCount ?? 0

/** Not finished: late, in the middle of, not started, or a quiz not passed. */
const isOpen = (i: LearningItem) => learnState(i) !== 'completed'
const t = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0)
/** In progress — started and not completed; a quiz not passed counts (handoff §2). */
const going = (i: LearningItem) => isOpen(i) && (notPassed(i) || (!!i.progress && i.progress.status !== 'not_started'))

/** Nothing at all until something is assigned — Home goes straight on to the next section (20 Oct 2026). */
export function YourLearning({ learnerOnly }: { learnerOnly: boolean }) {
  const orgs = useOrgs()
  const navigate = useNavigate()
  const { items } = useMyLearning(orgs.current?.id ?? null)
  const [now] = useState(() => Date.now())
  if (!items?.length) return null

  // The Continue card: the one most recently opened.
  const hero = items.filter(going).sort((a, b) => t(b.progress?.lastActiveAt) - t(a.progress?.lastActiveAt))[0] ?? null
  // Up next: everything still to do, the card's course aside — overdue, then soonest due, then no due
  // date (newest first); up to three.
  const rank = (i: LearningItem) => (learnState(i) === 'overdue' ? 0 : i.dueAt ? 1 : 2)
  const week = items.filter(i => i !== hero && isOpen(i))
    .sort((a, b) => rank(a) - rank(b) || (a.dueAt && b.dueAt ? t(a.dueAt) - t(b.dueAt) : t(b.receivedAt) - t(a.receivedAt))).slice(0, 3)
  const allHref = learnerOnly ? '/courses' : LEARNING_HOME
  const learn = (i: LearningItem, start: 'continue' | 'over') => navigate(`/learn/${i.launchId}`, { state: { backTo: '/', start } })

  if (!hero && !week.length) return (
    <section className="mb-10 rounded-[20px] border border-[#ECE9F0] bg-white px-6 py-5 text-[15px] text-gray-500">
      You’re all caught up — everything assigned to you is finished.{' '}
      <a {...spaLink(allHref, () => navigate(allHref))} className="font-semibold text-[#7A3BD9]">See your courses</a>
    </section>
  )

  return (
    // minmax(0, …) columns: the cards' one-line text must never widen the row past a narrow screen.
    <section className={['mb-10 grid grid-cols-[minmax(0,1fr)] items-stretch gap-5 sm:gap-6', hero && week.length ? 'min-[1100px]:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]' : ''].join(' ')}>
      {hero && <ContinueCard item={hero} now={now} alone={!week.length} onGo={() => learn(hero, notPassed(hero) ? 'over' : 'continue')} />}
      {week.length > 0 && (
        <div className="flex flex-col gap-3 rounded-[20px] border border-[#ECE9F0] bg-white px-6 py-[18px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[16px] font-semibold text-[#1C1A22]">Up next</h2>
            <a {...spaLink(allHref, () => navigate(allHref))} className="inline-flex items-center gap-1 text-[13px] text-[#6B6775] hover:text-[#7A3BD9]">See all <ArrowRight className="h-3.5 w-3.5" /></a>
          </div>
          {week.map(i => {
            const warn = learnState(i) === 'overdue' || notPassed(i)
            const when = i.dueAt ? `due ${due(i.dueAt, now)}` : 'no due date'
            const text = learnState(i) === 'overdue' ? `Overdue · was due ${dayMonth(i.dueAt!)}`
              : notPassed(i) ? `Quiz not passed · ${when}` : i.dueAt ? `Due ${due(i.dueAt, now)}` : `New · assigned ${dayMonth(i.receivedAt)}`
            return (
              <div key={i.launchId} className="flex items-center gap-3">
                <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: warn ? '#E08A1E' : '#7A3BD9' }} />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-[14.5px] font-medium text-[#1C1A22]">{i.title}</span>
                  <span className="truncate text-[13px]" style={{ color: warn ? '#C2610C' : '#6B6775' }}>{text}</span>
                </div>
                <a {...spaLink(`/learn/${i.launchId}`, () => learn(i, notPassed(i) ? 'over' : 'continue'))}
                  className="shrink-0 text-[14px] font-semibold text-[#7A3BD9] hover:text-[#5B21B6]">Open</a>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

/** The Continue card (warm sand). The whole card is the link; “Continue” is part of it. */
function ContinueCard({ item, now, alone, onGo }: { item: LearningItem; now: number; alone: boolean; onGo: () => void }) {
  const n = slides(item)
  // The furthest slide reached (22 Oct 2026) — the one the percentage counts, not one they went back to.
  const p = item.progress
  const at = Math.min(Math.max(p?.retake ? p.retake.currentSlide : p?.currentSlide ?? 0, (p?.retake ? p.retake.furthestSlide : p?.furthestSlide) ?? 0) + 1, Math.max(n, 1))
  const pct = Math.round(item.progress?.retake?.percent ?? item.progress?.percent ?? 0)
  const where = n ? `Slide ${at} of ${n}` : ''
  const when = item.dueAt ? (learnState(item) === 'overdue' ? `was due ${dayMonth(item.dueAt)}` : `due ${due(item.dueAt, now)}`) : ''
  // Reached the end with the quiz not passed (15 Oct 2026): not finished — say so, and Try again
  // starts a fresh attempt, rather than “Continue” reopening the last slide.
  const failed = notPassed(item) && !item.progress?.retake
  const score = item.progress?.score
  // "Course finished · Score: 20%" (27 Oct 2026, was "Your score 20%"). "Finished", not "completed":
  // completed means passed everywhere else (Reports, the Completed list), and this one is not.
  const meta = item.progressPending ? 'Updating your progress…' : [failed ? (score != null ? `Course finished · Score: ${Math.round(score)}%` : 'Quiz not passed') : where, when].filter(Boolean).join(' · ')
  return (
    <a {...spaLink(`/learn/${item.launchId}`, onGo)} aria-label={failed ? `Try ${item.title} again — quiz not passed` : `Continue ${item.title}${where ? `, ${where.toLowerCase()}` : ''}`}
      className={['group relative flex flex-wrap items-center gap-x-5 gap-y-3.5 overflow-hidden rounded-[18px] px-4 py-4 no-underline sm:flex-nowrap sm:rounded-[20px] sm:px-7 sm:py-5',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E07A1F]', alone ? 'sm:min-h-[132px]' : 'sm:min-h-[168px]'].join(' ')}
      style={{ background: 'linear-gradient(120deg, #FBF3E9 0%, #F7E8D6 100%)' }}>
      {/* The decoration: smaller and tucked further into the corner on a phone, so it sits behind the corner, not the text. */}
      <span aria-hidden className="pointer-events-none absolute -right-[70px] -top-[90px] h-[170px] w-[170px] rounded-full sm:-right-[100px] sm:-top-[120px] sm:h-[300px] sm:w-[300px]" style={{ background: 'rgba(242,140,40,.10)' }} />
      <span className={['relative grid shrink-0 place-items-center overflow-hidden rounded-[12px] bg-white h-12 w-12 sm:rounded-[14px]', alone ? 'sm:h-[68px] sm:w-[68px]' : 'sm:h-[76px] sm:w-[76px]'].join(' ')}>
        {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" className="h-full w-full object-cover" />
          : <BookOpen className={alone ? 'h-5 w-5 sm:h-6 sm:w-6' : 'h-5 w-5 sm:h-[26px] sm:w-[26px]'} color="#C2610C" strokeWidth={1.4} />}
      </span>
      <span className="relative flex min-w-0 flex-1 flex-col gap-1.5 sm:gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[.08em] text-[#B0703A] sm:text-[12px] sm:tracking-[.12em]">{failed ? 'Quiz not passed — try again' : 'Pick up where you left off'}</span>
        {/* Two lines on a phone, where one would cut most titles short; one line from sm up (handoff). */}
        <span className="line-clamp-2 text-[17px] font-semibold leading-snug text-[#2A1E12] sm:line-clamp-1 sm:text-[20px]">{item.title}</span>
        <span className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5">
          <span role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Progress"
            className={['h-1.5 w-full overflow-hidden rounded-full bg-[#EED9C0]', alone ? 'sm:w-[280px]' : 'sm:w-[220px]', item.progressPending ? 'lb-updating text-[#E07A1F]' : ''].join(' ')}>
            {!item.progressPending && <span className="block h-full rounded-full bg-[#E07A1F]" style={{ width: `${pct}%` }} />}
          </span>
          {meta && <span className="truncate text-[13px] text-[#7A6450] sm:text-[13.5px]">{meta}</span>}
        </span>
      </span>
      <span className="relative grid h-11 w-full shrink-0 place-items-center whitespace-nowrap rounded-full bg-[#1C1A22] px-6 text-[15px] font-semibold text-white group-hover:bg-[#2E2B35] sm:w-auto">{failed ? 'Try again' : 'Continue'}</span>
    </a>
  )
}
